/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { FoodmaxapipbSearchItemsResponse } from "generated/foodmaxapi.type"
import { SearchItemsRequest } from "queries/types"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategoryEng } from "constants/tracking"

/** RTK lazy `searchItems` trigger result shape used by menu item search flows */
export type MenuItemSearchRtkResult = {
  data?: FoodmaxapipbSearchItemsResponse
  error?: unknown
}

export interface PerformMenuItemSearchOptions {
  /** Matches `showLoading` on the pending SEARCH_ITEMS action and legacy thunk */
  showLoading: boolean
  /** Base action type, e.g. `actions.SEARCH_ITEMS` from the menu duck */
  searchItemsBaseAction: string
  dispatch: (action: unknown) => unknown | Promise<unknown>
  merchantID?: string
  merchantGroupID?: string
  isRtkFlagLoading: boolean
  isRtkSearchItemsEnabled: boolean
  searchItemsRtk: (arg: SearchItemsRequest) => Promise<MenuItemSearchRtkResult>
  dispatchLegacySearch: () => unknown | Promise<unknown>
}

/**
 * Runs menu item search via RTK Query or legacy Redux, dispatching hand-rolled
 * SEARCH_ITEMS_SUCCESS / SEARCH_ITEMS_FAIL shapes for the RTK path so they stay aligned with the reducer.
 */
export async function performMenuItemSearch(
  searchKey: string,
  options: PerformMenuItemSearchOptions,
): Promise<void> {
  const {
    showLoading,
    searchItemsBaseAction,
    dispatch,
    merchantID,
    merchantGroupID,
    isRtkFlagLoading,
    isRtkSearchItemsEnabled,
    searchItemsRtk,
    dispatchLegacySearch,
  } = options

  if (!isRtkFlagLoading && isRtkSearchItemsEnabled) {
    const actionSearchItems = {
      type: searchItemsBaseAction,
      showLoading,
    }

    dispatch(actionSearchItems)

    const result = await searchItemsRtk({
      merchantID,
      merchantGroupID,
      searchKey,
      needModifierGroups: true,
    })

    if (result.error) {
      dispatch({
        type: getActionFail(searchItemsBaseAction),
        payload: { data: {} },
        meta: {
          previousAction: {
            ...actionSearchItems,
            payload: { request: { data: {} } },
          },
        },
      })
    } else {
      // Always dispatch success when there is no error so searchItemLoading clears.
      // RTK can resolve with neither error nor truthy data (e.g. empty body); `else if (result.data)` would leave loading stuck.
      dispatch({
        type: getActionSuccess(searchItemsBaseAction),
        payload: {
          data: result?.data ?? {},
        },
        meta: {
          previousAction: {
            ...actionSearchItems,
            payload: { request: { data: {} } },
          },
        },
      })
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.searchItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSearchItemsEnabled,
        FROM_RTK_QUERY: true,
        FROM_OLD_REDUX: false,
        ACTION_NAME: "searchItems",
      },
    })
  } else if (!isRtkFlagLoading) {
    await Promise.resolve(dispatchLegacySearch())

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.searchItems,
      labels: {
        MERCHANT_ID: merchantID || "",
        IS_RTK_ENABLED: isRtkSearchItemsEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "searchItems",
      },
    })
  }
}
